import { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Button, Chip, Fade, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';
import { DetailAndDelBtn, MetrcLogo } from '../../utility';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import SearchIcon from '@mui/icons-material/Search';
import { metrcTagsClasses, roomDetailClasses, tableClasses, errFeedbackFn } from '../../utility';
import { FarmContext } from '../../../context/FarmContext';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import { useToken } from '../../../utility';
import { failsTokenAndSetupCheck } from '../../../checks/setup';
import CreateItemPopup from '../Popup/CreateItemPopup';
import ItemEntryPopup from './ItemEntryPopup';

export default function _ItemsTab(props) {
  const { farmContextValue } = useContext(FarmContext);

  const {
    currDarkThemeClass = '',
    openItemsPopup = false,
    values,
    itemPerms = null,
    handleSetValues,
    handleErrorWithFeedback = () => {},
    handleEntityEntryCreateRes = () => {},
    handleEntityEntryModifyRes = () => {},
    ...otherProps
  } = props;

  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [detailChildren, setDetailChildren] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [farmDetails, setFarmDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const feedbackCX = useContext(FeedbackContext);
  const { token, tokenDetails } = useToken();
  const invalidTokenState = useMemo(() => failsTokenAndSetupCheck(token, tokenDetails), [token]);

  const fetchFarmData = async (farm = selectedFarm, currentPage = page, limit = rowsPerPage, search = searchTerm) => {
    setLoading(true);
    try {
      let url = `item/?limit=${rowsPerPage}&page=${page + 1}`;
      if (selectedFilter !== 'All') {
        url += `&farm_id=${selectedFarm.farm_id}`;
      }
      if (searchTerm) {
        url += `&name__icontains=${encodeURIComponent(searchTerm)}`;
      }
      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const formattedData = formatData(response.data.results || []);
        setData(formattedData);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setLoading(false);
    }
  };

  // Function that syncs data to Metrc and then fetches the latest farm data
  const handleSyncToMetrc = async () => {
    if (selectedFarm) {
      setSyncLoading(true);
      let syncTometrcUrl = `item/?licenseNumber=${selectedFarm.license_number}&sync_to_metrc=true`;

      try {
        const response = await apiService.fetchInstance(syncTometrcUrl);

        // If the sync to Metrc is successful, fetch the latest farm data
        if (response.status === 200) {
          // Call fetchFarmData to reload the data
          fetchFarmData();

          // Provide feedback to the user
          feedbackCX.setContextValue(true, {
            type: 'success',
            message: 'Items Synced From Metrc Successfully.',
          });
          setTimeout(() => {
            feedbackCX.setContextValue(false, {});
          }, 5000);
        }
      } catch (error) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: `Error syncing to Metrc`,
        });
      } finally {
        setSyncLoading(false);
      }
    }
  };
  useEffect(() => {
    if (farmContextValue && Array.isArray(farmContextValue.farms) && farmContextValue.farms.length > 0) {
      setFarms(farmContextValue.farms);

      const defaultFarm = farmContextValue.farms[0];
      setSelectedFarm(defaultFarm);
      setSelectedFilter(defaultFarm.farm_id);
    }
  }, [farmContextValue]);

  useEffect(() => {
    if (invalidTokenState) return;
    if (selectedFarm) {
      fetchFarmData(selectedFarm, page, rowsPerPage, searchTerm, invalidTokenState);
    }
  }, [selectedFarm, page, rowsPerPage, searchTerm, invalidTokenState]);

  const formatData = formattedData => {
    return formattedData.map((item, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      ...itemKeyTitleMap.map(e => {
        // Check if the key is 'category_name', 'category_type', or 'quantity_type' and return the value accordingly
        if (
          e.key === 'item_name' ||
          e.key === 'category_name' ||
          e.key === 'category_type' ||
          e.key === 'quantity_type' ||
          e.key === 'unit_name' ||
          e.key === 'strain_name'
        ) {
          return item[e.key] || 'N/A'; // If the value is missing, show 'N/A'
        }
        return e.key !== 'item_name' ? +item[e.key] : item[e.key];
      }),

      <DetailAndDelBtn {...gDItemEntryActionBtnProps(item)} />,
    ]);
  };
  const handleEntityEntryModify = (entity, prop) => async value => {
    const errConfig = {
      modify: true,
      resource: 'Item',
    };
    let res = { success: false };
    try {
      res = await apiService.modifyInstance(entity, value).then(handleEntityEntryModifyRes(prop));
      fetchFarmData();
    } catch (err) {
      handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
    }
    return res;
  };
  const handleSetSelItem = (prop, value) => {
    setDetailChildren(state => ({
      ...state,
      selItem: {
        ...state.selItem,
        [prop]: value,
      },
    }));
  };

  const handleItemEntryView = async item => {
    setShowEditPopup(true);
    try {
      const unitRes = await apiService.fetchInstance('unit/');
      const unitList = unitRes?.data?.results || [];

      setDetailChildren({
        selItem: item,
        title: item.item_name,
        handleItemSave: handleEntityEntryModify(`item/${item.id}/`, 'item'),
        handleSetSelItem,
        unitList, // pass units to the popup
      });
    } catch (error) {
      console.error('Error fetching unit list for edit popup', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Unable to fetch units for editing.',
      });
    }
  };

  const gDItemEntryActionBtnProps = item => ({
    currDarkThemeClass,
    toShowDelBtn: false,
    // onDetailClick: null, // disables click
    onDetailClick: () => handleItemEntryView(item),
    // toShowEditBtn: true,
    toShowDetailBtn: true,
  });

  const itemKeyTitleMap = [
    { key: 'item_name', title: 'Name' },
    { key: 'category_name', title: 'Category Name' },
    { key: 'category_type', title: 'Category Type' },
    { key: 'quantity_type', title: 'Quantity Type' },
    { key: 'unit_name', title: 'Unit Name' },
    { key: 'strain_name', title: 'Strain Name' },
    // { key: 'action', title: ' ' },
  ];

  const addNewKey = 'add';
  const updateKey = 'update';
  const tableTitles = itemKeyTitleMap.map(e => ({ title: e.title }));

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };
  const handleEntitySave = (entity, prop) => async value => {
    const errConfig = {
      create: true,
      resource: 'Item',
    };

    let res = { success: false };

    try {
      const itemData = {
        ...value,
        farm: value.farm_id || selectedFarm.farm_id,
        farm_id: value.farm_id || selectedFarm.farm_id, // Adding farm_id from selectedFarm if it's not in value
      };
      res = await apiService.createInstance(entity, itemData).then(response => {
        handleEntityEntryCreateRes(prop)(response);
        fetchFarmData();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Created Item successfully.',
          autoHideDuration: 3000,
        });
        return { success: true };
      });
    } catch (err) {
      handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
    }
    return res;
  };

  const handleAddNewItem = async () => {
    // Set the popup open and pass the full farm details
    handleSetValues(addNewKey, true);
    setShowCreatePopup(true);

    // Find the farm object based on the selected farm ID
    const farmDetails = farms.find(f => f.farm_id === selectedFilter);
    const farmId = farmDetails?.farm_id;
    try {
      const response = await apiService.fetchInstance(`new/item/?farm_id=${farmId}`);
      const collectionData = response.data;
      // Pass the full farm object along with other details for new item creation
      setDetailChildren({
        title: 'New Item',
        farmDetails: farmDetails, // Full farm object
        collectionData,
        handleAddItem: handleEntitySave('item/', 'items'), // Item save function
      });
    } catch (error) {
      handleErrorWithFeedback(errFeedbackFn({ resource: 'Item Collection' }))(error);
    }
  };

  const handleFarmSelect = farmDetails => {
    setFarmDetails(farmDetails); // Update the state with the selected farm
  };

  const handlePopupClose = () => {
    // Reset form values
    handleSetValues('item_name', '');
    handleSetValues('category_name', '');
    handleSetValues('category_type', '');
    handleSetValues('quantity_type', '');
    handleSetValues('unit_name', '');
    handleSetValues('strain_name', '');
    handleSetValues('loading', false);
    handleSetValues('openItemsPopup', false);

    setDetailChildren({});
    // setOpen(false);
    setShowCreatePopup(false);
    setShowEditPopup(false);
    handleSetValues('openItemsPopup', false);
  };
  useEffect(() => {
    if (values?.triggerAddNewItem) {
      handleAddNewItem(); // Actually opens the popup and fetches data
      handleSetValues('triggerAddNewItem', false); // Reset to prevent re-triggers
    }
  }, [values?.triggerAddNewItem]);

  const itemPopupProps = {
    currDarkThemeClass,
    values,
    handleSetValues,
    open: showCreatePopup,
    selectedFarm,

    handlePopupClose,
    handleAddItem: handleEntitySave('item/', 'items'),
    onClose: handlePopupClose,
    ...detailChildren,
  };
  const itemEditPopupProps = {
    currDarkThemeClass,
    open: openItemsPopup || showEditPopup,
    farms,
    handlePopupClose,
    onClose: handlePopupClose,
    unitList: detailChildren?.collectionData?.unit || [],
    ...detailChildren,
  };

  const displayProps = {
    currDarkThemeClass,
    tableEntries: data,
    tableTitles,
    totalCount,
    onPageChange: handlePageChange,
    toShowAddBtn: false,
    loading,
    rowsPerPage,
    page,
    entity: 'Items',
    ...otherProps,
  };

  // Example values for the table props
  const itemTableValues = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Items Found',
    entity: 'Items',
    tableEntries: data,
    tableTitles: [{ title: 'Sl. No' }, ...itemKeyTitleMap.map(e => ({ title: e.title })), { title: '' }],
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading: false, // Set loading to true if data is being fetched
    onClick: handleAddNewItem,
    ...otherProps,
  };

  const handleFilterChange = farmId => {
    const farmObj = farms.find(f => f.farm_id === farmId);
    setSelectedFarm(farmObj);
    setSelectedFilter(farmId);
    setPage(0);
  };

  const sChipsProps = farm => ({
    key: farm.farm_id,
    label: farm.farm_name,
    variant: selectedFilter === farm.farm_id ? 'filled' : 'outlined',
    onClick: () => handleFilterChange(farm.farm_id),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });
  const metrcTagFarmChipsList = (
    <Stack direction="row" spacing={1}>
      {farms.length > 0 ? (
        farms.map(farm => (
          <Chip key={farm.farm_id} {...sChipsProps(farm)} /> // Render chip for each farm
        ))
      ) : (
        <Typography>No farms available</Typography>
      )}
    </Stack>
  );
  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };
  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Item Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps} sx={{ flex: 1, minWidth: 0 }}>
            <Typography {...metrcTagFilterTypoProps} sx={{ whiteSpace: 'nowrap' }}>
              Show by Farm:
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: 1,
                  minWidth: 'max-content',
                  pb: 0.5,
                }}
              >
                {metrcTagFarmChipsList}
              </Box>
            </Box>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...itemTableValues} />
        </Box>
        {showCreatePopup && addNewKey && <CreateItemPopup {...itemPopupProps} />}
        {showEditPopup && updateKey && <ItemEntryPopup {...itemEditPopupProps} />}

        {/* {isEditPopupOpen && (
            <MetrcTagsEditPopup  {...metrcTagsEditPopupProps} />
  )} */}
      </Box>
    </Fade>
  );
}
